export const WEI_PER_ETH = 1_000_000_000_000_000_000n;
// Legacy demo fixture pricing only. Live views and transactions use BE quotes.
export const BASE_MINT_FEE_WEI = 2_000_000_000_000_000n;
export const MINTS_PER_FEE_STEP = 300n;

export type MintFeeProgress = Readonly<{
  cumulativeTotalMinted: bigint;
  stepIndex: bigint;
  stepProgress: bigint;
  mintsRemaining: bigint;
  currentFeeWei: bigint;
  nextStepFeeWei: bigint;
}>;

export function calculateMintFeeProgress(cumulativeTotalMinted: bigint): MintFeeProgress {
  if (cumulativeTotalMinted < 0n) throw new RangeError("cumulativeTotalMinted cannot be negative");
  const stepIndex = cumulativeTotalMinted / MINTS_PER_FEE_STEP;
  const stepProgress = cumulativeTotalMinted % MINTS_PER_FEE_STEP;
  const currentFeeWei = BASE_MINT_FEE_WEI * (stepIndex + 1n);
  return Object.freeze({
    cumulativeTotalMinted,
    stepIndex,
    stepProgress,
    mintsRemaining: MINTS_PER_FEE_STEP - stepProgress,
    currentFeeWei,
    nextStepFeeWei: currentFeeWei + BASE_MINT_FEE_WEI,
  });
}

/** Exact decimal formatter. Display-only rounding is truncation, never financial state. */
export function formatUnits(value: bigint, decimals: number, maxFractionDigits = decimals): string {
  if (!Number.isSafeInteger(decimals) || decimals < 0) throw new RangeError("invalid decimals");
  if (!Number.isSafeInteger(maxFractionDigits) || maxFractionDigits < 0) {
    throw new RangeError("invalid maxFractionDigits");
  }
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const scale = 10n ** BigInt(decimals);
  const integer = absolute / scale;
  const fraction = absolute % scale;
  const kept = Math.min(decimals, maxFractionDigits);
  const digits = kept === 0 ? "" : fraction.toString().padStart(decimals, "0").slice(0, kept).replace(/0+$/, "");
  return `${negative ? "-" : ""}${integer.toString()}${digits ? `.${digits}` : ""}`;
}

export function formatEth(wei: bigint, maxFractionDigits = 6): string {
  return formatUnits(wei, 18, maxFractionDigits);
}

export function parseUnsignedDecimal(value: string, label: string): bigint {
  if (!/^(0|[1-9][0-9]*)$/.test(value)) throw new TypeError(`${label} must be a canonical unsigned decimal string`);
  return BigInt(value);
}

export function rewardBpsLabel(rewardBps: number | null): string {
  if (rewardBps === null) return "Pending";
  if (!Number.isSafeInteger(rewardBps) || rewardBps < 0 || rewardBps > 10_000) {
    throw new RangeError("rewardBps is out of range");
  }
  const whole = Math.floor(rewardBps / 100);
  const fraction = String(rewardBps % 100).padStart(2, "0").replace(/0+$/, "");
  return `${whole}${fraction ? `.${fraction}` : ""}%`;
}

export function abbreviateHex(value: string, edge = 4): string {
  if (value.length <= edge * 2 + 2) return value;
  return `${value.slice(0, edge + 2)}…${value.slice(-edge)}`;
}
